import { useState } from 'react';

import { api } from '../../services/api.js';
import { useChat } from '../../hooks/useChat.js';

export type LeftView =
  | 'chats'
  | 'groups'
  | 'communities'
  | 'channels'
  | 'status'
  | 'ads'
  | 'catalog'
  | 'templates';

type NavItem = {
  key: LeftView;
  icon: string;
  label: string;
  badge?: number;
};

type Props = {
  activeView: LeftView;
  onViewChange: (view: LeftView) => void;
};

const LeftSidebar = ({ activeView, onViewChange }: Props) => {
  const { accounts, reload } = useChat();
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);

  const activeAccount = accounts.find((a) => a.isActive) ?? accounts[0];

  const navItems: NavItem[] = [
    { key: 'chats', icon: '💬', label: 'Chats' },
    { key: 'groups', icon: '👥', label: 'Groepen' },
    { key: 'communities', icon: '🏘️', label: 'Communities' },
    { key: 'channels', icon: '📢', label: 'Kanalen' },
    { key: 'status', icon: '🟢', label: 'Status' },
    { key: 'ads', icon: '📣', label: 'Ads' },
    { key: 'catalog', icon: '🛍️', label: 'Catalogus' },
    { key: 'templates', icon: '📝', label: 'Templates' },
  ];

  const handleAccountActivate = async (accountId: string) => {
    try {
      await api.activateAccount(accountId);
      await reload();
      setShowAccountSwitcher(false);
    } catch {
      // ignore
    }
  };

  return (
    <aside className="left-sidebar" aria-label="Navigatie">
      <div className="left-sidebar-account">
        <button
          type="button"
          className="account-switcher-btn"
          onClick={() => setShowAccountSwitcher((prev) => !prev)}
          title={activeAccount?.label ?? 'Geen account'}
        >
          <span className="account-avatar-mini">
            {activeAccount?.label.slice(0, 1).toUpperCase() ?? '?'}
          </span>
          <span className={`account-status-dot ${activeAccount?.status.state ?? 'disconnected'}`} />
        </button>
        {showAccountSwitcher && (
          <div className="account-switcher-dropdown" role="menu">
            {accounts.length === 0 && (
              <p className="muted small" style={{ padding: '0.5rem' }}>Geen accounts</p>
            )}
            {accounts.map((account) => (
              <button
                key={account.id}
                type="button"
                className={`account-switch-item ${account.isActive ? 'active' : ''}`}
                onClick={() => void handleAccountActivate(account.id)}
                role="menuitem"
              >
                <span className="account-avatar-mini">
                  {account.label.slice(0, 1).toUpperCase()}
                </span>
                <span className="account-switch-info">
                  <strong>{account.label}</strong>
                  <small className={`status-text ${account.status.state}`}>
                    {account.status.state}
                  </small>
                </span>
                {account.isActive && <span className="active-dot" />}
              </button>
            ))}
          </div>
        )}
      </div>

      <nav className="left-sidebar-nav" aria-label="Hoofdnavigatie">
        {navItems.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`left-nav-item ${activeView === item.key ? 'active' : ''}`}
            onClick={() => onViewChange(item.key)}
            title={item.label}
            aria-label={item.label}
            aria-current={activeView === item.key ? 'page' : undefined}
          >
            <span className="left-nav-icon" aria-hidden="true">{item.icon}</span>
            <span className="left-nav-label" aria-hidden="true">{item.label}</span>
            {item.badge !== undefined && item.badge > 0 && (
              <span className="left-nav-badge">{item.badge}</span>
            )}
          </button>
        ))}
      </nav>

      <div className="left-sidebar-footer">
        <button
          type="button"
          className="left-nav-item"
          title="Instellingen"
          aria-label="Instellingen"
          onClick={() => { window.location.hash = '/settings'; }}
        >
          <span className="left-nav-icon" aria-hidden="true">⚙️</span>
          <span className="left-nav-label" aria-hidden="true">Instellingen</span>
        </button>
      </div>
    </aside>
  );
};

export default LeftSidebar;
